import React from 'react';
import { useLocation } from 'react-router-dom';
import CollegeJourneyPage from './CollegeJourneyPage';
import CollegeJourneyNavigation from './CollegeJourneyNavigation';
import { useMilestoneCompletionTriggers } from '../../hooks/useMilestoneCompletionTriggers';

/**
 * Wrapper component that provides the unified College Journey header
 * for all College Journey pages/routes
 */
const CollegeJourneyWrapper = ({ children }) => {
  const location = useLocation();
  
  // Initialize milestone completion detection triggers
  useMilestoneCompletionTriggers();
  
  // Check if we're on a College Journey route
  const isCollegeJourneyRoute = location.pathname.startsWith('/college');
  
  // For the main college routes that use tabs, use CollegeJourneyPage
  const mainTabRoutes = [
    '/college',
    '/college/dashboard',
    '/college/profile',
    '/college/colleges',
    '/college/colleges/compare',
    '/college/colleges/tier-analysis',
    '/college/colleges/portfolio-strategy',
    '/college/essays',
    '/college/timeline',
    '/college/financial-aid'
  ];

  // Routes that should show tabs with custom content (not using CollegeJourneyPage)
  const tabRoutesWithCustomContent = [
    '/college/recommendations',
    '/college/comparison',
    '/college/brag-sheet',
    '/college/assessment-analysis',
    '/college/strategic-assessment'
  ];
  
  // Routes that should show tabs but render their own content (not CollegeJourneyPage)
  const tabsWithCustomContent = [
    '/college/strategic-profile/',  // Handles /college/strategic-profile/:id
    '/college/colleges/',            // Handles /college/colleges/:collegeId (individual strategy pages)
    '/college/enhanced-strategy/'    // Legacy route - handles /college/enhanced-strategy/:collegeId
  ];
  
  // Check if current route is one of the main tab routes
  const isMainTabRoute = mainTabRoutes.some(route => {
    // Exact match or home route match
    if (location.pathname === route || (route === '/college' && location.pathname === '/college/')) {
      return true;
    }
    // For comparison sub-routes, match if pathname starts with the route
    if (route.includes('/compare') || route.includes('/tier-analysis') || route.includes('/portfolio-strategy')) {
      return location.pathname.startsWith(route);
    }
    return false;
  });

  // Special comparison routes that should NOT be treated as individual college pages
  const comparisonRoutes = [
    '/college/colleges/compare',
    '/college/colleges/tier-analysis',
    '/college/colleges/portfolio-strategy'
  ];

  const isComparisonRoute = comparisonRoutes.some(route =>
    location.pathname.startsWith(route)
  );

  // Check if this is an individual college strategy page (has a UUID in the path)
  // Exclude comparison routes which may have hyphens but aren't UUIDs
  const isIndividualCollegePage = !isComparisonRoute && (
    /^\/college\/colleges\/[a-f0-9-]{36}/.test(location.pathname) ||
    /^\/college\/enhanced-strategy\/[a-f0-9-]{36}/.test(location.pathname)
  );

  // Check if route should show tabs with custom content
  const shouldShowTabsWithContent =
    isIndividualCollegePage ||
    tabsWithCustomContent.some(pattern =>
      location.pathname.startsWith(pattern) && !isMainTabRoute
    ) ||
    tabRoutesWithCustomContent.some(route => location.pathname === route);
  
  if (isMainTabRoute) {
    // For main tab routes, use CollegeJourneyPage which includes the tab navigation
    return <CollegeJourneyPage />;
  }
  
  if (shouldShowTabsWithContent) {
    // For routes that need tabs but have their own content
    // Individual college pages manage their own padding/layout
    if (isIndividualCollegePage) {
      return (
        <div className="min-h-screen bg-stone-50">
          <CollegeJourneyNavigation />
          {children}
        </div>
      );
    }

    // Other custom content routes get standard padding
    return (
      <div className="min-h-screen bg-stone-50">
        <CollegeJourneyNavigation />
        <div className="px-4 py-4 space-y-4 pb-20">
          {children}
        </div>
      </div>
    );
  }
  
  if (isCollegeJourneyRoute) {
    // For other College Journey routes, wrap with the header but no tabs
    return (
      <div className="min-h-screen bg-stone-50">
        {/* Header */}
        <div className="sticky top-0 z-20 bg-white border-b border-stone-200">
          <div className="px-4 py-3">
            {/* Top row: Back button, Section name with color bar */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => window.history.back()}
                  className="p-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
                  style={{ minWidth: '44px', minHeight: '44px' }}
                  aria-label="Go back"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <div className="flex items-center gap-3">
                  {/* Color bar - using violet for college journey */}
                  <div className="w-2 h-6 bg-violet-500 rounded-full"></div>
                  <div>
                    <h1 className="text-xl font-bold text-slate-900 tracking-tight">College Journey</h1>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* End Header */}

        {/* Content */}
        <div className="px-4 py-4 space-y-4 pb-20">
          {children}
        </div>
      </div>
    );
  }
  
  // For non-College Journey routes, just render children
  return children;
};

export default CollegeJourneyWrapper;